import { Plus, Settings2 } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, GridHookForm } from 'vnetwork-ui'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getSupplierOptions } from '@/modules/Contract/modules/Partner/services'
import type { Supplier } from '@/modules/Contract/modules/Partner/types'
import { Contract } from '@/modules/Contract/types'

const CURRENCY_OPTIONS = [
    { label: 'VND', value: 'VND' },
    { label: 'USD', value: 'USD' },
    { label: 'EUR', value: 'EUR' },
    { label: 'JPY', value: 'JPY' },
]

const CustomConfigSection: React.FC = () => {
    const { t } = useTranslation('contract')
    const formControl = useFormContext<Contract>()
    const { setModal } = useModal()

    const supplierOptionsQuery = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.SUPPLIERS, 'options'],
            queryFn: getSupplierOptions,
        },
        { showError: false, showLoading: false }
    )

    const supplierOptions =
        supplierOptionsQuery.data?.map((supplier) => ({
            label: supplier.partner_code
                ? `${supplier.partner_name} (${supplier.partner_code})`
                : supplier.partner_name,
            value: supplier.partner_id,
        })) || []

    const openSupplierModal = () => {
        setModal('ContractModals/SupplierModal/index', {
            mode: 'create',
            onCreated: (supplier: Supplier) => {
                supplierOptionsQuery.refetch()
                formControl.setValue('partner_id', supplier.partner_id, {
                    shouldDirty: true,
                    shouldValidate: true,
                })
            },
        })
    }

    return (
        <div
            data-tour="custom-config-section"
            className="border border-gray-200 bg-gray-50/30 rounded-md overflow-hidden"
        >
            {/* Header */}
            <div className="flex items-center gap-2 p-3">
                <Settings2 className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-900">
                    {t('custom_settings')}
                </span>
            </div>

            {/* Content */}
            <div className="border-t border-gray-200 p-3 bg-white/50">
                <GridHookForm
                    subForm
                    formControl={formControl}
                    actions={[]}
                    className="grid-cols-4 gap-3"
                    data={[
                        {
                            formItemProps: {
                                name: 'currency_code',
                                type: 'select',
                                label: t('currency_code'),
                                options: CURRENCY_OPTIONS,
                                allowClear: false,
                            },
                            className: 'col-span-3',
                        },
                        {
                            formItemProps: {
                                name: 'exchange_rate',
                                type: 'number',
                                label: t('exchange_rate'),
                            },
                            className: 'col-span-1',
                        },
                        // The "use_contract_id" switch used to sit here: it turned this form into a
                        // fourth way of creating an annex (see CreateCustomerSubcontract on the
                        // API). Removed on request — annexes are now made from the parent
                        // contract's Phụ lục tab, either "Tạo phụ lục" or "Chuyển hợp đồng thành
                        // phụ lục". The field itself is still sent as false so the request shape
                        // and the server branch are untouched.
                        {
                            formItemProps: {
                                name: 'partner_id',
                                type: 'select',
                                label: t('partner_name'),
                                options: supplierOptions,
                                allowClear: true,
                            },
                            className: 'col-span-4',
                        },
                        {
                            renderEditComponent: () => (
                                <Button
                                    type="button"
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Plus className="h-4 w-4" />}
                                    onClick={openSupplierModal}
                                    className="w-full"
                                >
                                    {t('create-supplier')}
                                </Button>
                            ),
                            className: 'col-span-4',
                        },
                    ]}
                />
            </div>
        </div>
    )
}

export default CustomConfigSection
